import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell } from "recharts";
import { TrendingUp, TrendingDown, Loader2, ArrowUpRight, ArrowDownRight, X, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const PERIODS = [
  { id: 7,   label: "7 days" },
  { id: 30,  label: "30 days" },
  { id: 90,  label: "90 days" },
  { id: 180, label: "6 months" },
];

const shortDate = (iso) => {
  // "2026-02-14" → "Feb 14"
  try {
    const [_, m, d] = iso.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${months[parseInt(m) - 1]} ${parseInt(d)}`;
  } catch { return iso; }
};

const formatMinor = (minor, cur) => {
  const d = cur?.decimals ?? 2;
  const val = (minor || 0) / Math.pow(10, d);
  const parts = val.toFixed(d).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${cur?.symbol || ""}${parts.join(".")}`;
};

/**
 * CashFlowChart — interactive daily inflow / outflow bar chart with a period
 * selector and counterpart-account breakdown. Uses recharts (already installed).
 */
export default function CashFlowChart({ compact = false }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [drilldownCode, setDrilldownCode] = useState(null);   // account_code being drilled

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/erp/reports/cash-flow?days=${days}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (loading && !data) return <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center text-stone-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading cash-flow…</div>;
  if (!data) return null;

  const { series, totals, currency, inflows_by_counterpart, outflows_by_counterpart } = data;
  const chartData = series.map(r => ({
    date: r.date,
    label: shortDate(r.date),
    inflow: r.inflow_paise,
    outflow: r.outflow_paise,
    // Store the outflow as a negative number so the bar renders below the axis
    // for an instantly-readable "up-good / down-bad" chart. Tooltip shows the
    // absolute value from `outflow`.
    outflowNeg: -r.outflow_paise,
    net: r.net_paise,
  }));

  const netPositive = totals.net_paise >= 0;

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="cashflow-chart">
      {/* Header */}
      <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Cash flow</div>
          <div className="text-sm text-stone-700">{data.period.from} → {data.period.to} · <span className="font-mono">{currency.code}</span></div>
        </div>
        <div className="flex gap-1 shrink-0" data-testid="cashflow-period-picker">
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setDays(p.id)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${days === p.id ? "bg-emerald-600 text-white border-emerald-600" : "border-stone-200 text-stone-600 hover:border-emerald-500 hover:text-emerald-700"}`}
              data-testid={`cashflow-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals strip */}
      <div className="px-5 py-3 grid grid-cols-3 gap-3 border-b border-stone-100 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center justify-center gap-1"><ArrowUpRight size={12} /> Inflow</div>
          <div className="font-mono font-black text-emerald-700 text-sm md:text-base" data-testid="cashflow-total-inflow">{formatMinor(totals.inflow_paise, currency)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-red-700 flex items-center justify-center gap-1"><ArrowDownRight size={12} /> Outflow</div>
          <div className="font-mono font-black text-red-700 text-sm md:text-base" data-testid="cashflow-total-outflow">{formatMinor(totals.outflow_paise, currency)}</div>
        </div>
        <div>
          <div className={`text-[10px] uppercase tracking-widest font-bold flex items-center justify-center gap-1 ${netPositive ? "text-emerald-700" : "text-red-700"}`}>
            {netPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />} Net
          </div>
          <div className={`font-mono font-black text-sm md:text-base ${netPositive ? "text-emerald-700" : "text-red-700"}`} data-testid="cashflow-total-net">
            {netPositive ? "+" : ""}{formatMinor(totals.net_paise, currency)}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-3 md:p-5" data-testid="cashflow-chart-area">
        <ResponsiveContainer width="100%" height={compact ? 180 : 260}>
          <BarChart
            data={chartData}
            stackOffset="sign"
            margin={{ top: 10, right: 8, bottom: 4, left: -10 }}
            onMouseMove={(e) => setHoveredIdx(e && e.activeTooltipIndex != null ? e.activeTooltipIndex : null)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
            <XAxis
              dataKey="label"
              stroke="#78716c"
              fontSize={10}
              interval="preserveStartEnd"
              tick={{ fill: "#78716c" }}
            />
            <YAxis
              stroke="#78716c"
              fontSize={10}
              tickFormatter={v => {
                const abs = Math.abs(v) / Math.pow(10, currency.decimals);
                if (abs >= 1e5) return `${currency.symbol}${(abs / 1e5).toFixed(1)}L`;
                if (abs >= 1e3) return `${currency.symbol}${(abs / 1e3).toFixed(0)}k`;
                return `${currency.symbol}${abs.toFixed(0)}`;
              }}
            />
            <ReferenceLine y={0} stroke="#57534e" />
            <Tooltip
              cursor={{ fill: "#f5f5f4" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div className="bg-white border border-stone-300 rounded-xl shadow-lg p-2 text-xs" data-testid="cashflow-tooltip">
                    <div className="font-bold text-stone-800 mb-1">{p.label}</div>
                    <div className="text-emerald-700 font-mono">▲ Inflow  {formatMinor(p.inflow, currency)}</div>
                    <div className="text-red-700 font-mono">▼ Outflow {formatMinor(p.outflow, currency)}</div>
                    <div className={`font-mono font-bold border-t border-stone-100 pt-1 mt-1 ${p.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      Net {p.net >= 0 ? "+" : ""}{formatMinor(p.net, currency)}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="inflow" stackId="cash" name="Inflow" radius={[3, 3, 0, 0]}>
              {chartData.map((_, i) => (
                <Cell key={`in-${i}`} fill={i === hoveredIdx ? "#059669" : "#10b981"} />
              ))}
            </Bar>
            <Bar dataKey="outflowNeg" stackId="cash" name="Outflow" radius={[0, 0, 3, 3]}>
              {chartData.map((_, i) => (
                <Cell key={`out-${i}`} fill={i === hoveredIdx ? "#b91c1c" : "#ef4444"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Counterpart breakdown */}
      {!compact && (inflows_by_counterpart.length > 0 || outflows_by_counterpart.length > 0) && (
        <div className="px-5 py-4 border-t border-stone-100 grid md:grid-cols-2 gap-4" data-testid="cashflow-breakdown">
          <BreakdownList
            title="Top inflow sources"
            rows={inflows_by_counterpart.slice(0, 5)}
            total={totals.inflow_paise}
            cur={currency}
            tone="emerald"
            onDrilldown={setDrilldownCode}
          />
          <BreakdownList
            title="Top spend categories"
            rows={outflows_by_counterpart.slice(0, 5)}
            total={totals.outflow_paise}
            cur={currency}
            tone="red"
            onDrilldown={setDrilldownCode}
          />
        </div>
      )}

      {drilldownCode && (
        <DrilldownModal
          accountCode={drilldownCode}
          days={days}
          cur={currency}
          onClose={() => setDrilldownCode(null)}
        />
      )}
    </div>
  );
}

function BreakdownList({ title, rows, total, cur, tone, onDrilldown }) {
  const barBg = tone === "emerald" ? "bg-emerald-500" : "bg-red-500";
  const dot   = tone === "emerald" ? "text-emerald-700" : "text-red-700";
  return (
    <div>
      <div className={`text-[11px] uppercase tracking-widest font-bold ${dot} mb-2`}>{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-stone-500 italic">No entries in this period.</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map(r => {
            const pct = total > 0 ? Math.max(2, Math.round((r.amount_paise / total) * 100)) : 0;
            return (
              <li key={r.code}>
                <button
                  type="button"
                  onClick={() => onDrilldown?.(r.code)}
                  className="w-full text-left group hover:bg-stone-50 rounded-md px-1 py-0.5 -mx-1 transition-colors"
                  data-testid={`cashflow-drilldown-${r.code}`}
                  title={`See every ${r.code} transaction in this period`}
                >
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="capitalize font-bold text-stone-800 group-hover:text-emerald-700 flex items-center gap-1">
                      {r.code}
                      <ChevronRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    <span className="font-mono text-stone-700">{formatMinor(r.amount_paise, cur)}</span>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barBg}`} style={{ width: `${pct}%` }} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DrilldownModal({ accountCode, days, cur, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/erp/reports/cash-flow/drilldown?account_code=${accountCode}&days=${days}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [accountCode, days]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="cashflow-drilldown-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Cash flow drilldown</div>
            <div className="font-bold text-stone-900 truncate">
              {data?.account?.name || accountCode}
              <span className="text-xs text-stone-500 font-normal ml-2">({data?.account?.type || "…"})</span>
            </div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="cashflow-drilldown-close">
            <X size={20} />
          </button>
        </div>

        {loading || !data ? (
          <div className="p-12 text-center text-stone-500"><Loader2 className="inline animate-spin mr-1" size={16} /> Loading…</div>
        ) : (
          <>
            <div className="px-5 py-3 grid grid-cols-4 gap-3 border-b border-stone-100 text-center text-xs bg-stone-50">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Entries</div>
                <div className="font-mono font-black">{data.totals.entry_count}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Inflow</div>
                <div className="font-mono font-black text-emerald-700">{formatMinor(data.totals.inflow_paise, cur)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">Outflow</div>
                <div className="font-mono font-black text-red-700">{formatMinor(data.totals.outflow_paise, cur)}</div>
              </div>
              <div>
                <div className={`text-[10px] uppercase tracking-widest font-bold ${data.totals.net_paise >= 0 ? "text-emerald-700" : "text-red-700"}`}>Net</div>
                <div className={`font-mono font-black ${data.totals.net_paise >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {data.totals.net_paise >= 0 ? "+" : ""}{formatMinor(data.totals.net_paise, cur)}
                </div>
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {data.entries.length === 0 ? (
                <div className="p-8 text-center text-sm text-stone-500">No transactions in this period.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="text-left">Description</th>
                      <th className="text-left">Cash side</th>
                      <th className="text-right pr-4">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {data.entries.map(e => (
                      <tr key={e.journal_id} className="hover:bg-stone-50" data-testid={`cashflow-drilldown-entry-${e.journal_id}`}>
                        <td className="px-4 py-2 text-xs text-stone-500 font-mono whitespace-nowrap">{e.date}</td>
                        <td className="text-stone-800">{e.description || <span className="italic text-stone-400">(no note)</span>}</td>
                        <td className="text-xs capitalize font-bold text-stone-600">{e.cash_side}</td>
                        <td className={`text-right font-mono font-bold pr-4 whitespace-nowrap ${e.direction === "inflow" ? "text-emerald-700" : "text-red-700"}`}>
                          {e.direction === "inflow" ? "+" : "−"}{formatMinor(e.amount_paise, cur)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="px-5 py-3 text-[11px] text-stone-500 border-t border-stone-100">
              {data.period.from} → {data.period.to} · {data.period.days} days
            </div>
          </>
        )}
      </div>
    </div>
  );
}
